import { html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { TrackingElement } from "./TrackingElement";

@customElement("survaq-image-carousel")
class SurvaqImageCarousel extends TrackingElement {
  @state() currentIndex = 0;

  @query("#slidesContainer") private slidesContainer?: HTMLElement;

  @state() private slides: HTMLElement[] = [];

  private dragging = false;
  private isTransitioning = false;
  private startPosX = 0;
  private currentTranslate = 0;
  private prevTranslate = 0;
  private animationID = 0;
  private slideRatio = 0.8;
  private slideGap = 0;

  firstUpdated() {
    const slot = this.shadowRoot?.querySelector("slot");
    if (slot) {
      this.slides = slot.assignedElements().filter((el) => el instanceof HTMLElement);

      this.slides.forEach((slide) => {
        slide.setAttribute("draggable", "false");
        slide.querySelectorAll("img").forEach((img) => {
          img.setAttribute("draggable", "false");
          (img as HTMLElement).style.userSelect = "none";
          (img as HTMLElement).style.pointerEvents = "none";
        });
      });

      this.updateCarousel();
    }

    window.addEventListener("resize", () => this.updateCarousel());

    this.slidesContainer?.addEventListener("transitionend", this.onTransitionEnd);
  }

  updateCarousel() {
    if (this.slidesContainer) {
      const containerWidth = this.offsetWidth;
      const slideWidth = containerWidth * this.slideRatio;
      this.slidesContainer.style.width = `${(this.slides.length + 2) * (slideWidth + this.slideGap)}px`;

      this.slidesContainer.innerHTML = "";

      const firstClone = this.slides[0]?.cloneNode(true) as HTMLElement;
      const lastClone = this.slides[this.slides.length - 1]?.cloneNode(true) as HTMLElement;

      const extendedSlides = [lastClone, ...this.slides, firstClone];

      extendedSlides.forEach((slide) => {
        slide.style.width = `${slideWidth}px`;
        slide.style.marginRight = `${this.slideGap}px`;
        slide.style.height = "100%";
        slide.style.objectFit = "cover";
        this.slidesContainer?.appendChild(slide);
      });

      this.setPositionByIndex(true);

      this.slidesContainer.removeEventListener("transitionend", this.onTransitionEnd);
      this.slidesContainer.addEventListener("transitionend", this.onTransitionEnd);
    }
  }

  setPositionByIndex(skipTransition = false) {
    if (this.isTransitioning && !skipTransition) return;

    const containerWidth = this.offsetWidth;
    const slideWidth = containerWidth * this.slideRatio;

    const domIndex = this.currentIndex + 1;

    this.currentTranslate =
      -(domIndex * (slideWidth + this.slideGap)) + (containerWidth - slideWidth) / 2;
    this.prevTranslate = this.currentTranslate;

    if (this.slidesContainer) {
      if (skipTransition) {
        this.slidesContainer.style.transition = "none";
        this.isTransitioning = false;
      } else {
        this.slidesContainer.style.transition = "transform 0.3s ease-out";
        this.isTransitioning = true;
      }

      this.setSliderPosition();
    }
  }

  pointerDown(event: TouchEvent | MouseEvent) {
    if (this.isTransitioning) return;

    this.dragging = true;
    this.startPosX = this.getPositionX(event);
    this.animationID = requestAnimationFrame(this.animation.bind(this));

    window.addEventListener("mousemove", this.pointerMove);
    window.addEventListener("mouseup", this.pointerUp);
    window.addEventListener("touchmove", this.pointerMove, { passive: false });
    window.addEventListener("touchend", this.pointerUp);
  }

  pointerMove = (event: TouchEvent | MouseEvent) => {
    if (!this.dragging) return;

    // 画面スクロールを防止する
    if (event.cancelable) event.preventDefault();

    const currentPosition = this.getPositionX(event);
    this.currentTranslate = this.prevTranslate + currentPosition - this.startPosX;
  };

  pointerUp = () => {
    this.dragging = false;
    cancelAnimationFrame(this.animationID);

    const movedBy = this.currentTranslate - this.prevTranslate;

    if (movedBy < -50) this.currentIndex++;
    if (movedBy > 50) this.currentIndex--;

    this.setPositionByIndex();
    this.isTransitioning = true;

    window.removeEventListener("mousemove", this.pointerMove);
    window.removeEventListener("mouseup", this.pointerUp);
    window.removeEventListener("touchmove", this.pointerMove);
    window.removeEventListener("touchend", this.pointerUp);
  };

  onTransitionEnd = () => {
    this.isTransitioning = false;
    const slideCount = this.slides.length;

    if (this.currentIndex >= slideCount) {
      this.currentIndex = 0;
      this.setPositionByIndex(true);
    } else if (this.currentIndex < 0) {
      this.currentIndex = slideCount - 1;
      this.setPositionByIndex(true);
    }
  };

  getPositionX(event: TouchEvent | MouseEvent) {
    return event instanceof TouchEvent ? (event.touches[0]?.clientX ?? 0) : event.clientX;
  }

  animation() {
    this.setSliderPosition();
    if (this.dragging) requestAnimationFrame(this.animation.bind(this));
  }

  setSliderPosition() {
    if (this.slidesContainer) {
      this.slidesContainer.style.transform = `translateX(${this.currentTranslate}px)`;
    }
  }

  render() {
    return html`
      ${this.renderTopMarker()}
      ${
        this.sneak
          ? ""
          : html`
            <div
              class="relative w-full overflow-hidden"
              @mousedown=${this.pointerDown}
              @touchstart=${this.pointerDown}
            >
              <div class="h-full flex items-center" id="slidesContainer"></div>
              <slot style="display:none;"></slot>
              <div class="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-2 backdrop-blur-lg rounded-full p-1">
                ${this.slides.map(
                  (_, index) => html`
                  <button
                    class="size-2 rounded-full ${this.currentIndex === index ? "bg-black" : "bg-gray-300"}"
                    @click="${() => {
                      this.currentIndex = index;
                      this.setPositionByIndex();
                    }}"
                  ></button>
                `,
                )}
              </div>
            </div>`
      }
      ${this.renderBottomMarker()}
    `;
  }
}

export { SurvaqImageCarousel };
